import { Component, OnInit, inject, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { TabsModule } from 'primeng/tabs';
import { TagModule } from 'primeng/tag';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { CatalogService, MarketplaceLaunchReadinessSummary } from './catalog.service';

@Component({
  standalone: true,
  selector: 'ain-marketplace-launch-readiness',
  imports: [
    AinPageComponent,
    AinStateComponent,
    InfoHintComponent,
    TableModule,
    ButtonModule,
    TabsModule,
    TagModule,
    RouterLink
  ],
  template: `
    <ain-page
      [title]="t('catalog.marketplace.launchReadiness.title')"
      [description]="t('catalog.marketplace.launchReadiness.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'catalog.marketplace.launchReadiness.help.title'"
        [descriptionKey]="'catalog.marketplace.launchReadiness.help.description'"
        [bulletKeys]="[
          'catalog.marketplace.launchReadiness.help.bullets.goNoGo',
          'catalog.marketplace.launchReadiness.help.bullets.config',
          'catalog.marketplace.launchReadiness.help.bullets.evidence',
          'catalog.marketplace.launchReadiness.help.bullets.runbooks'
        ]" />

      @if (loading()) {
        <ain-state type="loading" [title]="t('catalog.loading')" />
      } @else if (error()) {
        <ain-state type="error" [title]="t('catalog.marketplace.launchReadiness.unavailable')" [message]="error() ?? ''" />
      } @else if (summary()) {
        <section class="panel summary-cards">
          <div class="card">
            <span class="label">{{ t('catalog.marketplace.launchReadiness.decision') }}</span>
            <p-tag [severity]="decisionSeverity(summary()!.launchDecision)" [value]="summary()!.launchDecision" />
          </div>
          <div class="card">
            <span class="label">{{ t('catalog.marketplace.launchReadiness.rolloutStage') }}</span>
            <strong>{{ summary()!.rolloutStage }}</strong>
          </div>
          <div class="card">
            <span class="label">{{ t('catalog.marketplace.launchReadiness.ready') }}</span>
            <strong>{{ summary()!.readyCount }}</strong>
          </div>
          <div class="card">
            <span class="label">{{ t('catalog.marketplace.launchReadiness.conditional') }}</span>
            <strong>{{ summary()!.conditionalCount }}</strong>
          </div>
          <div class="card">
            <span class="label">{{ t('catalog.marketplace.launchReadiness.blockers') }}</span>
            <strong>{{ summary()!.blockerCount }}</strong>
          </div>
        </section>

        <section class="toolbar">
          <button pButton type="button" [label]="t('common.refresh')" (click)="reload()"></button>
          <a pButton type="button" class="p-button-outlined" [routerLink]="['/payments/financial-completeness']"
             [label]="t('catalog.marketplace.launchReadiness.linkFinancialCompleteness')"></a>
          <a pButton type="button" class="p-button-outlined" [routerLink]="['/platform/user-party-bindings']"
             [label]="t('catalog.marketplace.launchReadiness.linkBindings')"></a>
          <a pButton type="button" class="p-button-outlined" [routerLink]="['/marketplace/global-commerce']"
             [label]="t('catalog.marketplace.launchReadiness.linkGlobalCommerce')"></a>
          <a pButton type="button" class="p-button-outlined" [routerLink]="['/marketplace/optimization']"
             [label]="t('catalog.marketplace.launchReadiness.linkOptimization')"></a>
        </section>

        @if (summary()!.launchBlockers.length > 0) {
          <ain-state type="error"
            [title]="t('catalog.marketplace.launchReadiness.blockersTitle')"
            [message]="summary()!.launchBlockers.join('; ')" />
        }

        <p-tabs value="goNoGo">
          <p-tablist>
            <p-tab value="goNoGo">{{ t('catalog.marketplace.launchReadiness.tabs.goNoGo') }}</p-tab>
            <p-tab value="evidence">{{ t('catalog.marketplace.launchReadiness.tabs.evidence') }}</p-tab>
            <p-tab value="seeded">{{ t('catalog.marketplace.launchReadiness.tabs.seeded') }}</p-tab>
            <p-tab value="performance">{{ t('catalog.marketplace.launchReadiness.tabs.performance') }}</p-tab>
            <p-tab value="observability">{{ t('catalog.marketplace.launchReadiness.tabs.observability') }}</p-tab>
            <p-tab value="providers">{{ t('catalog.marketplace.launchReadiness.tabs.providers') }}</p-tab>
            <p-tab value="config">{{ t('catalog.marketplace.launchReadiness.tabs.config') }}</p-tab>
            <p-tab value="kpi">{{ t('catalog.marketplace.launchReadiness.tabs.kpi') }}</p-tab>
            <p-tab value="hypercare">{{ t('catalog.marketplace.launchReadiness.tabs.hypercare') }}</p-tab>
            <p-tab value="runbooks">{{ t('catalog.marketplace.launchReadiness.tabs.runbooks') }}</p-tab>
            <p-tab value="deferrals">{{ t('catalog.marketplace.launchReadiness.tabs.deferrals') }}</p-tab>
          </p-tablist>
          <p-tabpanels>
            <p-tabpanel value="goNoGo">
              <p-table [value]="summary()!.goNoGoCriteria" [paginator]="true" [rows]="15">
                <ng-template pTemplate="header">
                  <tr>
                    <th>{{ t('catalog.marketplace.launchReadiness.columns.domain') }}</th>
                    <th>{{ t('catalog.marketplace.launchReadiness.columns.owner') }}</th>
                    <th>{{ t('catalog.marketplace.launchReadiness.columns.threshold') }}</th>
                    <th>{{ t('catalog.marketplace.launchReadiness.columns.status') }}</th>
                    <th>{{ t('catalog.marketplace.launchReadiness.columns.notes') }}</th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                  <tr>
                    <td>{{ row.domain }} ({{ row.key }})</td>
                    <td>{{ row.owner }}</td>
                    <td>{{ row.threshold }}</td>
                    <td><p-tag [severity]="statusSeverity(row.status)" [value]="row.status" /></td>
                    <td>{{ row.notes ?? '—' }}</td>
                  </tr>
                </ng-template>
              </p-table>
            </p-tabpanel>
            <p-tabpanel value="evidence">
              <p class="tab-intro">{{ t('catalog.marketplace.launchReadiness.evidenceIntro') }}</p>
              <p-table [value]="summary()!.operationalEvidence" [paginator]="true" [rows]="10">
                <ng-template pTemplate="header">
                  <tr>
                    <th>{{ t('catalog.marketplace.launchReadiness.columns.evidence') }}</th>
                    <th>{{ t('catalog.marketplace.launchReadiness.columns.status') }}</th>
                    <th>{{ t('catalog.marketplace.launchReadiness.columns.lastRecorded') }}</th>
                    <th>{{ t('catalog.marketplace.launchReadiness.columns.reference') }}</th>
                    <th>{{ t('catalog.marketplace.launchReadiness.columns.notes') }}</th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                  <tr>
                    <td>{{ row.label }}</td>
                    <td><p-tag [severity]="evidenceSeverity(row.status)" [value]="row.status" /></td>
                    <td>{{ row.lastRecordedAtUtc ?? '—' }}</td>
                    <td>{{ row.evidenceReference ?? '—' }}</td>
                    <td>{{ row.notes ?? '—' }}</td>
                  </tr>
                </ng-template>
              </p-table>
              <section class="drill-panel">
                <h3>{{ t('catalog.marketplace.launchReadiness.syntheticDrillTitle') }}</h3>
                <p>{{ summary()!.syntheticCheckoutDrill.evidenceRule }}</p>
                <p><strong>{{ t('catalog.marketplace.launchReadiness.columns.lastRecorded') }}:</strong>
                  {{ summary()!.syntheticCheckoutDrill.lastRecordedAtUtc }}</p>
                <ul>
                  @for (check of summary()!.syntheticCheckoutDrill.requiredChecks; track check) {
                    <li><code>{{ check }}</code></li>
                  }
                </ul>
              </section>
            </p-tabpanel>
            <p-tabpanel value="seeded">
              <p-table [value]="summary()!.seededConfigStatus" [paginator]="true" [rows]="12">
                <ng-template pTemplate="header">
                  <tr>
                    <th>{{ t('catalog.marketplace.launchReadiness.columns.config') }}</th>
                    <th>{{ t('catalog.marketplace.launchReadiness.columns.current') }}</th>
                    <th>{{ t('catalog.marketplace.launchReadiness.columns.status') }}</th>
                    <th>{{ t('catalog.marketplace.launchReadiness.columns.notes') }}</th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                  <tr>
                    <td>{{ row.label }}</td>
                    <td>{{ row.currentValue }}</td>
                    <td><p-tag [severity]="seededSeverity(row.status)" [value]="row.status" /></td>
                    <td>{{ row.notes ?? '—' }}</td>
                  </tr>
                </ng-template>
              </p-table>
            </p-tabpanel>
            <p-tabpanel value="performance">
              <h3>{{ t('catalog.marketplace.launchReadiness.performanceBudgetsTitle') }}</h3>
              <p-table [value]="summary()!.performanceBudgets" [paginator]="false">
                <ng-template pTemplate="header">
                  <tr>
                    <th>{{ t('catalog.marketplace.launchReadiness.columns.kpi') }}</th>
                    <th>{{ t('catalog.marketplace.launchReadiness.columns.p95Target') }}</th>
                    <th>{{ t('catalog.marketplace.launchReadiness.columns.measurement') }}</th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                  <tr>
                    <td>{{ row.label }}</td>
                    <td>{{ row.p95TargetMs }} ms</td>
                    <td>{{ row.measurementSource }}</td>
                  </tr>
                </ng-template>
              </p-table>
              <h3>{{ t('catalog.marketplace.launchReadiness.cacheBudgetsTitle') }}</h3>
              <p-table [value]="summary()!.cacheBudgets" [paginator]="false">
                <ng-template pTemplate="header">
                  <tr>
                    <th>{{ t('catalog.marketplace.launchReadiness.columns.kpi') }}</th>
                    <th>{{ t('catalog.marketplace.launchReadiness.columns.maxStaleness') }}</th>
                    <th>{{ t('catalog.marketplace.launchReadiness.columns.notes') }}</th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                  <tr>
                    <td>{{ row.label }}</td>
                    <td>{{ row.maxStalenessSeconds }} s</td>
                    <td>{{ row.notes }}</td>
                  </tr>
                </ng-template>
              </p-table>
              <h3>{{ t('catalog.marketplace.launchReadiness.sloCodesTitle') }}</h3>
              <ul class="runbook-list">
                @for (code of summary()!.sloBreachReasonCodes; track code) {
                  <li><code>{{ code }}</code></li>
                }
              </ul>
            </p-tabpanel>
            <p-tabpanel value="observability">
              <p-table [value]="summary()!.observabilityChecks" [paginator]="true" [rows]="10">
                <ng-template pTemplate="header">
                  <tr>
                    <th>{{ t('catalog.marketplace.launchReadiness.columns.kpi') }}</th>
                    <th>{{ t('catalog.marketplace.launchReadiness.columns.endpoint') }}</th>
                    <th>{{ t('catalog.marketplace.launchReadiness.columns.status') }}</th>
                    <th>{{ t('catalog.marketplace.launchReadiness.columns.notes') }}</th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                  <tr>
                    <td>{{ row.label }}</td>
                    <td><code>{{ row.endpointOrSource }}</code></td>
                    <td><p-tag [severity]="observabilitySeverity(row.status)" [value]="row.status" /></td>
                    <td>{{ row.notes ?? '—' }}</td>
                  </tr>
                </ng-template>
              </p-table>
            </p-tabpanel>
            <p-tabpanel value="providers">
              <p-table [value]="summary()!.providerModes" [paginator]="false">
                <ng-template pTemplate="header">
                  <tr>
                    <th>{{ t('catalog.marketplace.launchReadiness.columns.domain') }}</th>
                    <th>{{ t('catalog.marketplace.launchReadiness.columns.mode') }}</th>
                    <th>{{ t('catalog.marketplace.launchReadiness.columns.status') }}</th>
                    <th>{{ t('catalog.marketplace.launchReadiness.columns.notes') }}</th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                  <tr>
                    <td>{{ row.domain }}</td>
                    <td>{{ row.mode }}</td>
                    <td><p-tag [severity]="seededSeverity(row.status)" [value]="row.status" /></td>
                    <td>{{ row.notes ?? '—' }}</td>
                  </tr>
                </ng-template>
              </p-table>
            </p-tabpanel>
            <p-tabpanel value="config">
              <p-table [value]="summary()!.configChecks" [paginator]="true" [rows]="15">
                <ng-template pTemplate="header">
                  <tr>
                    <th>{{ t('catalog.marketplace.launchReadiness.columns.config') }}</th>
                    <th>{{ t('catalog.marketplace.launchReadiness.columns.current') }}</th>
                    <th>{{ t('catalog.marketplace.launchReadiness.columns.expected') }}</th>
                    <th>{{ t('catalog.marketplace.launchReadiness.columns.status') }}</th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                  <tr>
                    <td>{{ row.label }}</td>
                    <td>{{ row.currentValue }}</td>
                    <td>{{ row.expectedProductionValue }}</td>
                    <td><p-tag [severity]="statusSeverity(row.status)" [value]="row.status" /></td>
                  </tr>
                </ng-template>
              </p-table>
            </p-tabpanel>
            <p-tabpanel value="kpi">
              <p-table [value]="summary()!.kpiBaselines" [paginator]="true" [rows]="15">
                <ng-template pTemplate="header">
                  <tr>
                    <th>{{ t('catalog.marketplace.launchReadiness.columns.kpi') }}</th>
                    <th>{{ t('catalog.marketplace.launchReadiness.columns.owner') }}</th>
                    <th>{{ t('catalog.marketplace.launchReadiness.columns.threshold') }}</th>
                    <th>{{ t('catalog.marketplace.launchReadiness.columns.alerts') }}</th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                  <tr>
                    <td>{{ row.label }}</td>
                    <td>{{ row.owner }}</td>
                    <td>{{ row.threshold }}</td>
                    <td>{{ row.alertContractStatus }}</td>
                  </tr>
                </ng-template>
              </p-table>
            </p-tabpanel>
            <p-tabpanel value="hypercare">
              <div class="hypercare-panel">
                <p><strong>{{ t('catalog.marketplace.launchReadiness.hypercare.window') }}:</strong>
                  {{ summary()!.hypercareSchedule.windowDays }} {{ t('catalog.marketplace.launchReadiness.hypercare.days') }}</p>
                <p><strong>{{ t('catalog.marketplace.launchReadiness.hypercare.cadence') }}:</strong>
                  {{ summary()!.hypercareSchedule.cadence }}</p>
                <p><strong>{{ t('catalog.marketplace.launchReadiness.hypercare.escalation') }}:</strong>
                  {{ summary()!.hypercareSchedule.escalationPath }}</p>
                <p>{{ summary()!.hypercareSchedule.notes }}</p>
              </div>
            </p-tabpanel>
            <p-tabpanel value="runbooks">
              <ul class="runbook-list">
                @for (key of summary()!.runbookKeys; track key) {
                  <li>
                    <code>{{ key }}</code>
                    — {{ t('catalog.marketplace.launchReadiness.runbookDoc') }}
                    <code>15-test-observability-runbooks.md</code>
                  </li>
                }
              </ul>
            </p-tabpanel>
            <p-tabpanel value="deferrals">
              <ul class="runbook-list">
                @for (item of summary()!.acceptedDeferrals; track item) {
                  <li><code>{{ item }}</code></li>
                }
              </ul>
            </p-tabpanel>
          </p-tabpanels>
        </p-tabs>
      }
    </ain-page>
  `,
  styles: [`
    .summary-cards { display: grid; grid-template-columns: repeat(auto-fit, minmax(140px, 1fr)); gap: 1rem; margin-bottom: 1rem; }
    .summary-cards .card { background: var(--surface-card); border: 1px solid var(--surface-border); border-radius: 8px; padding: 1rem; display: flex; flex-direction: column; gap: 0.5rem; }
    .summary-cards .label { color: var(--text-color-secondary); font-size: 0.875rem; }
    .toolbar { display: flex; flex-wrap: wrap; gap: 0.75rem; margin-bottom: 1rem; }
    .runbook-list { margin: 0; padding-left: 1.25rem; line-height: 1.8; }
    .tab-intro { margin-bottom: 1rem; color: var(--text-color-secondary); }
    .drill-panel, .hypercare-panel { margin-top: 1.5rem; padding: 1rem; border: 1px solid var(--surface-border); border-radius: 8px; }
    h3 { margin: 1rem 0 0.5rem; font-size: 1rem; }
  `]
})
export class MarketplaceLaunchReadinessComponent implements OnInit {
  private readonly catalogService = inject(CatalogService);
  private readonly localization = inject(LocalizationService);

  readonly loading = signal(true);
  readonly error = signal<string | null>(null);
  readonly summary = signal<MarketplaceLaunchReadinessSummary | null>(null);

  ngOnInit(): void {
    this.reload();
  }

  reload(): void {
    this.loading.set(true);
    this.error.set(null);
    this.catalogService.getMarketplaceLaunchReadiness().subscribe({
      next: row => {
        this.summary.set(row);
        this.loading.set(false);
      },
      error: err => {
        this.error.set(err?.error?.error ?? err?.message ?? 'launch_readiness_unavailable');
        this.loading.set(false);
      }
    });
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  decisionSeverity(decision: string): 'success' | 'warn' | 'danger' | 'info' {
    if (decision === 'Go') return 'success';
    if (decision === 'Conditional Go') return 'warn';
    if (decision === 'No-Go') return 'danger';
    return 'info';
  }

  statusSeverity(status: string): 'success' | 'warn' | 'danger' | 'info' {
    if (status === 'pass') return 'success';
    if (status === 'conditional') return 'warn';
    if (status === 'fail') return 'danger';
    return 'info';
  }

  evidenceSeverity(status: string): 'success' | 'warn' | 'danger' | 'info' {
    if (status === 'recorded') return 'success';
    if (status === 'stale') return 'warn';
    if (status === 'missing') return 'danger';
    return 'info';
  }

  seededSeverity(status: string): 'success' | 'warn' | 'danger' | 'info' {
    if (status === 'ready' || status === 'configured') return 'success';
    if (status === 'conditional' || status === 'internal') return 'warn';
    if (status === 'blocker' || status === 'missing') return 'danger';
    return 'info';
  }

  observabilitySeverity(status: string): 'success' | 'warn' | 'danger' | 'info' {
    if (status === 'wired' || status === 'contract-ready' || status === 'provider-wired') return 'success';
    if (status === 'conditional' || status === 'log-backed') return 'warn';
    return 'info';
  }
}
